/**
 * Personal Notes Component
 * Reads highlights, comments and sermons from the shared Firebase project (bible-app-365f7)
 * — the same data stored by sidebyside-bible.com —
 * and displays them in the Social Bible Study app.
 */

import { db } from '../firebase.js';
import { auth } from '../firebase.js';
import {
    collection, query, where, getDocs, orderBy
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

// ─── Mapping verseId (format "Book-ch-v") to human-readable ────────────────
// bible-app stores verseId as e.g. "Genesisy-1-3"
const BOOK_MG_TO_FR = {
    'Genesisy': 'Genèse', 'Eksodosy': 'Exode', 'Levitikosy': 'Lévitique',
    'Nomery': 'Nombres', 'Deotoronomia': 'Deutéronome', 'Josoa': 'Josué',
    'Mpitsara': 'Juges', 'Rota': 'Ruth',
    '1Samoela': '1 Samuel', '2Samoela': '2 Samuel',
    '1Mpanjaka': '1 Rois', '2Mpanjaka': '2 Rois',
    '1Tantara': '1 Chroniques', '2Tantara': '2 Chroniques',
    'Ezra': 'Esdras', 'Nehemia': 'Néhémie', 'Estera': 'Esther', 'Joba': 'Job',
    'Salamo': 'Psaumes', 'Ohabolana': 'Proverbes', 'Mpitoriteny': 'Ecclésiaste',
    'TononkIranI': 'Cantique', 'Isaia': 'Ésaïe', 'Jeremia': 'Jérémie',
    'Fitomaniana': 'Lamentations', 'Ezekiela': 'Ézéchiel', 'Daniela': 'Daniel',
    'Hosea': 'Osée', 'Joela': 'Joël', 'Amosa': 'Amos', 'Obadia': 'Abdias',
    'Jona': 'Jonas', 'Mika': 'Michée', 'Nahoma': 'Nahum', 'Habakoka': 'Habacuc',
    'Zefania': 'Sophonie', 'Hagay': 'Aggée', 'Zakaria': 'Zacharie', 'Malakia': 'Malachie',
    'Matio': 'Matthieu', 'Marka': 'Marc', 'Lioka': 'Luc', 'Jaona': 'Jean',
    'AsanNyApostoly': 'Actes', 'Romana': 'Romains',
    '1Korintiana': '1 Corinthiens', '2Korintiana': '2 Corinthiens',
    'Galatiana': 'Galates', 'Efesiana': 'Éphésiens', 'Filipiana': 'Philippiens',
    'Kolosiana': 'Colossiens',
    '1Tesaloniana': '1 Thessaloniciens', '2Tesaloniana': '2 Thessaloniciens',
    '1Timoty': '1 Timothée', '2Timoty': '2 Timothée',
    'Titosy': 'Tite', 'Filemona': 'Philémon', 'Hebreo': 'Hébreux',
    'Jakoba': 'Jacques',
    '1Petera': '1 Pierre', '2Petera': '2 Pierre',
    '1Jaona': '1 Jean', '2Jaona': '2 Jean', '3Jaona': '3 Jean',
    'Joda': 'Jude', 'Apokalypsy': 'Apocalypse',
};

const COLOR_LABELS = {
    blue: { label: 'Important', color: '#6366f1', emoji: '🔵' },
    red: { label: 'À étudier', color: '#ef4444', emoji: '🔴' },
    green: { label: 'Promesse', color: '#22c55e', emoji: '🟢' },
};

let container = null;

// ─── Public API ──────────────────────────────────────────────────────────────

export async function initPersonalNotes(el) {
    container = el;
    await renderNotes();
}

export function cleanupPersonalNotes() {
    container = null;
}

// Attend que Firebase Auth soit résolu (évite la race condition)
function waitForUser() {
    return new Promise((resolve) => {
        if (auth.currentUser) {
            resolve(auth.currentUser);
            return;
        }
        const unsub = onAuthStateChanged(auth, (user) => {
            unsub();
            resolve(user);
        });
    });
}

// ─── Main render ─────────────────────────────────────────────────────────────

async function renderNotes() {
    if (!container) return;

    // Afficher le skeleton pendant qu'on attend l'auth
    container.innerHTML = `
        <div class="notes-header">
            <div>
                <h2>📝 Notes Personnelles</h2>
                <p class="notes-subtitle">Vos surlignages, commentaires et prédications depuis SideBySide Bible</p>
            </div>
            <button class="btn btn-secondary" id="refresh-notes-btn">🔄 Actualiser</button>
        </div>
        <div id="notes-tabs" class="notes-tabs">
            <button class="notes-tab-btn active" data-tab="highlights">🎨 Surlignages</button>
            <button class="notes-tab-btn" data-tab="comments">💬 Commentaires</button>
            <button class="notes-tab-btn" data-tab="sermons">✍️ Prédications</button>
        </div>
        <div id="notes-content" class="notes-content">
            <div class="loading">⏳ Chargement de vos notes...</div>
        </div>
    `;

    container.querySelector('#refresh-notes-btn').addEventListener('click', renderNotes);
    container.querySelectorAll('.notes-tab-btn').forEach(btn => {
        btn.addEventListener('click', () => switchTab(btn.dataset.tab));
    });

    // Attendre que Firebase Auth soit résolu (évite race condition)
    const user = await waitForUser();
    console.log('[PersonalNotes] Auth resolved, user:', user?.email || 'none');

    if (!user) {
        showNotLoggedIn();
        return;
    }

    await loadAndShowTab('highlights', user.uid);
}

function switchTab(tab) {
    const user = auth.currentUser;
    if (!user) return;

    container.querySelectorAll('.notes-tab-btn').forEach(b => {
        b.classList.toggle('active', b.dataset.tab === tab);
    });

    loadAndShowTab(tab, user.uid);
}

// ─── Data loading ─────────────────────────────────────────────────────────────

async function loadAndShowTab(tab, userId) {
    const content = container.querySelector('#notes-content');
    content.innerHTML = '<div class="loading">⏳ Chargement...</div>';

    try {
        switch (tab) {
            case 'highlights': {
                const highlights = await fetchHighlights(userId);
                renderHighlights(highlights, content);
                break;
            }
            case 'comments': {
                const comments = await fetchComments(userId);
                renderComments(comments, content);
                break;
            }
            case 'sermons': {
                const sermons = await fetchSermons(userId);
                renderSermons(sermons, content);
                break;
            }
        }
    } catch (err) {
        console.error('[PersonalNotes] Error:', err);
        content.innerHTML = `
            <div class="error-state">
                <p>❌ Erreur lors du chargement. Vérifiez votre connexion.</p>
                <small style="color:var(--text-muted)">${err.message}</small>
            </div>`;
    }
}

async function fetchHighlights(userId) {
    const q = query(
        collection(db, 'highlights'),
        where('userId', '==', userId)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

async function fetchComments(userId) {
    const q = query(
        collection(db, 'comments'),
        where('userId', '==', userId)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

async function fetchSermons(userId) {
    const q = query(
        collection(db, 'sermons'),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ─── Renderers ────────────────────────────────────────────────────────────────

function renderHighlights(highlights, el) {
    if (highlights.length === 0) {
        el.innerHTML = emptyState('🎨', 'Aucun surlignage', 'Surlignez des versets dans SideBySide Bible pour les retrouver ici.');
        return;
    }

    // Group by color
    const byColor = {};
    highlights.forEach(h => {
        const c = h.color || 'blue';
        if (!byColor[c]) byColor[c] = [];
        byColor[c].push(h);
    });

    el.innerHTML = `
        <div class="notes-summary">
            ${Object.entries(byColor).map(([c, items]) => {
        const meta = COLOR_LABELS[c] || { label: c, emoji: '⭕', color: '#888' };
        return `<span class="notes-badge" style="background:${meta.color}20;border:1px solid ${meta.color}40;color:${meta.color}">
                    ${meta.emoji} ${items.length} ${meta.label}
                </span>`;
    }).join('')}
        </div>
        <div class="notes-list">
            ${highlights.map(h => {
        const meta = COLOR_LABELS[h.color] || COLOR_LABELS.blue;
        const ref = formatVerseId(h.verseId || '');
        return `
                <div class="note-card note-card--highlight">
                    <div class="note-card-bar" style="background:${meta.color}"></div>
                    <div class="note-card-body">
                        <span class="note-ref">📖 ${escHtml(ref)}</span>
                        <span class="notes-badge" style="background:${meta.color}20;border:1px solid ${meta.color}40;color:${meta.color};font-size:0.7rem">
                            ${meta.emoji} ${meta.label}
                        </span>
                    </div>
                </div>`;
    }).join('')}
        </div>
    `;
}

function renderComments(comments, el) {
    if (comments.length === 0) {
        el.innerHTML = emptyState('💬', 'Aucun commentaire', 'Ajoutez des commentaires sur des versets dans SideBySide Bible.');
        return;
    }

    el.innerHTML = `
        <div class="notes-list">
            ${comments.map(c => {
        const ref = formatVerseId(c.verseId || c.verse_id || '');
        const date = formatDate(c.updatedAt || c.createdAt);
        return `
                <div class="note-card">
                    <div class="note-card-bar" style="background:var(--primary)"></div>
                    <div class="note-card-body">
                        <div class="note-card-header">
                            <span class="note-ref">📖 ${escHtml(ref)}</span>
                            <span class="note-date">${date}</span>
                        </div>
                        <p class="note-text">${escHtml(c.content || '')}</p>
                    </div>
                </div>`;
    }).join('')}
        </div>
    `;
}

function renderSermons(sermons, el) {
    if (sermons.length === 0) {
        el.innerHTML = emptyState('✍️', 'Aucune prédication', 'Créez des prédications dans SideBySide Bible pour les retrouver ici.');
        return;
    }

    el.innerHTML = `
        <div class="notes-list">
            ${sermons.map(s => {
        const date = formatDate(s.updatedAt || s.createdAt);
        const preview = (s.content || '').replace(/<[^>]*>/g, '').slice(0, 120);
        const typeLabel = s.type === 'homelies' ? '🎤 Homélie' : s.type === 'free' ? '📝 Libre' : '✍️ Prédication';
        return `
                <div class="note-card note-card--sermon">
                    <div class="note-card-bar" style="background:var(--accent-gold)"></div>
                    <div class="note-card-body">
                        <div class="note-card-header">
                            <span class="note-sermon-type">${typeLabel}</span>
                            <span class="note-date">${date}</span>
                        </div>
                        <h4 class="note-sermon-title">${escHtml(s.title || 'Sans titre')}</h4>
                        ${preview ? `<p class="note-text">${escHtml(preview)}${s.content && s.content.length > 120 ? '…' : ''}</p>` : ''}
                    </div>
                </div>`;
    }).join('')}
        </div>
    `;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatVerseId(verseId) {
    if (!verseId) return 'Verset inconnu';
    // Format: "Genesisy-1-3" → "Genèse 1:3"
    const parts = verseId.split('-');
    if (parts.length < 2) return verseId;
    const book = parts[0];
    const chapter = parts[1];
    const verse = parts[2];
    const frBook = BOOK_MG_TO_FR[book] || book;
    return verse ? `${frBook} ${chapter}:${verse}` : `${frBook} ${chapter}`;
}

function formatDate(ts) {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function escHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
}

function emptyState(icon, title, desc) {
    return `
        <div class="empty-state" style="padding:3rem 1rem">
            <div style="font-size:3rem;margin-bottom:1rem">${icon}</div>
            <h3>${title}</h3>
            <p style="margin-top:.5rem;color:var(--text-muted)">${desc}</p>
            <a href="https://sidebyside-bible.com" target="_blank" class="btn btn-secondary" style="margin-top:1.5rem;display:inline-flex">
                📖 Ouvrir SideBySide Bible
            </a>
        </div>`;
}

function showNotLoggedIn() {
    const content = container.querySelector('#notes-content');
    if (content) {
        content.innerHTML = `
            <div class="empty-state" style="padding:3rem 1rem">
                <div style="font-size:3rem;margin-bottom:1rem">🔐</div>
                <h3>Connexion requise</h3>
                <p style="margin-top:.5rem;color:var(--text-muted)">Connectez-vous pour accéder à vos notes personnelles.</p>
            </div>`;
    }
}
